import ListCard, { CardWrapper } from '@Components/ListCard/ListCard';
import EditAdminModal from '@Components/Modals/EditAdmin/EditAdminModal';
import RadioList from '@Components/RadioList/RadioList';
import usePagination from '@Hooks/usePagination';
import AdminService from '@Services/Admin.services.ts';
import { Admin } from '@Types/Admin.types';
import { KGOffice } from '@Types/Office.types';
import { PaginatedResponse } from '@Types/Response.types';
import { KGPortalUser } from '@Types/User.type';
import {
  COMPANIES,
  FIRST_PAGE_NUMBER,
  PAGE_PROP,
  PAGE_SIZE,
  PAGINATED_DEFAULT,
  PATHS,
  PERSISTENT_FILTER_PROP,
  SEARCH_PROP,
  SEARCH_TIMER_INTERVAL,
  TAB_PROP,
  TAB_VALUES,
  TABLE_STRUCTURES,
  USER_FILTER_PARAMS,
  USER_TAB_VALUES,
} from '@Utils/constants';
import { formatCompanyRadioOptions, formatOfficeRadioOptions } from '@Utils/format.utils';
import { clearInputs } from '@Utils/input.utils';
import { fillSearchParams } from '@Utils/navigation.utils';
import { handleRequestError } from '@Utils/request.utils';
import { useApolloClient } from '@apollo/client';
import { Button, ErrorDisplay, Pagination, SearchBox, SectionCard, useTopHat } from '@kg-internal/pv3-uikit';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { GetEmployeesDocument, GetEmployeesQuery, GetOfficesDocument, GetOfficesQuery } from '~/__generated__/graph';
import { createSingleSearchParameter } from '~/Utils/url.util';
import styles from './Users.module.scss';

const Users = () => {
  const block = 'users';
  const client = useApolloClient();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [users, setUsers] = useState<KGPortalUser[]>([]);
  const [offices, setOffices] = useState<KGOffice[]>([]);
  const [selectedOffice, setSelectedOffice] = useState<number | undefined>(
    searchParams.has(USER_FILTER_PARAMS.OFFICE) ? parseInt(searchParams.get(USER_FILTER_PARAMS.OFFICE)!) : undefined,
  );
  const [selectedCompany, setSelectedCompany] = useState<number | undefined>(
    searchParams.has(USER_FILTER_PARAMS.COMPANY) ? parseInt(searchParams.get(USER_FILTER_PARAMS.COMPANY)!) : undefined,
  );
  const [admins, setAdmins] = useState<PaginatedResponse<Admin>>(PAGINATED_DEFAULT);
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const [currentPage, setCurrentPage] = useState<number>(() =>
    searchParams.has(PAGE_PROP) ? parseInt(searchParams.get(PAGE_PROP) || '', 10) : 1,
  );
  const { currentData, jump } = usePagination(users, PAGE_SIZE, undefined);
  const [currentTab, setCurrentTab] = useState<number>(
    searchParams.has(TAB_PROP) ? parseInt(searchParams.get(TAB_PROP) || '', 10) : TAB_VALUES.USERS,
  );
  const [search, setSearch] = useState<string>(searchParams.get(SEARCH_PROP) || '');
  const [title, setTitle] = useState<string>(currentTab === TAB_VALUES.USERS ? 'Users' : 'Admins');
  const [selectedAdmin, setSelectedAdmin] = useState<Admin | undefined>();
  const [showEditAdminModal, setShowEditAdminModal] = useState<boolean>(false);

  const { setTopHat } = useTopHat({
    activeNav: 'nav-users',
    activeTab: currentTab === TAB_VALUES.USERS ? 'tab-users' : 'tab-admins',
    tabs: USER_TAB_VALUES,
    breadcrumbs: [
      {
        title: 'Users',
        // when on user page, make clicking the tophat not change URL
        href: `.`,
        id: 'Users',
      },
    ],
  });
  let timer: number;

  const mapUserFilters = ({
    tab = currentTab,
    page = currentPage,
    company = selectedCompany,
    office = selectedOffice,
    searchFilter = search,
  }) => ({
    [PAGE_PROP]: page,
    [TAB_PROP]: tab,
    [USER_FILTER_PARAMS.COMPANY]: company,
    [USER_FILTER_PARAMS.OFFICE]: office,
    [SEARCH_PROP]: searchFilter,
  });

  const moreDetailUrlSearchParam = useMemo(() => {
    return createSingleSearchParameter(PERSISTENT_FILTER_PROP, mapUserFilters({}));
  }, [searchParams]);

  const getUsers = async (page?: number) => {
    setLoading(true);
    try {
      const { data } = await client.query<GetEmployeesQuery>({
        query: GetEmployeesDocument,
        variables: { page: page || 1, office: selectedOffice, company: selectedCompany },
      });

      if (data && data.peopleV2) {
        setUsers(data.peopleV2);
        setErrorMessage(undefined);
      }
    } catch (_error) {
      setErrorMessage('Error loading data');
    } finally {
      setLoading(false);
    }
  };

  const getAdmins = (page?: number) => {
    AdminService.getAdmins(page || currentPage, true)
      .then((response) => {
        setAdmins(response.data);
        setErrorMessage(undefined);
      })
      .catch((error) => {
        handleRequestError(error, setErrorMessage);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const searchUsers = async (toSearch?: string, page?: number) => {
    setLoading(true);
    try {
      const { data } = await client.query<GetEmployeesQuery>({
        query: GetEmployeesDocument,
        variables: { page: page || 1, office: selectedOffice, company: selectedCompany },
      });
      if (data && data.peopleV2) {
        let filteredUsers = data.peopleV2;
        if (toSearch) {
          filteredUsers = filteredUsers.filter((user) => user.fullName!.toLowerCase().includes(toSearch.toLowerCase()));
        }
        setUsers(filteredUsers);
        setErrorMessage(undefined);
      }
    } catch (_error) {
      setErrorMessage('Error loading data');
    } finally {
      setLoading(false);
    }
  };

  const searchAdmins = (toSearch: string, page?: number) => {
    AdminService.getAdminSearch({ search: toSearch }, page || currentPage)
      .then((response) => {
        setAdmins(response.data);
        setErrorMessage(undefined);
      })
      .catch((error) => {
        handleRequestError(error, setErrorMessage);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleSearch = (value: string) => {
    // Handle search
    setLoading(true);
    // Wait until user is done typing to make search
    clearTimeout(timer);
    timer = window.setTimeout(() => {
      if (currentTab === TAB_VALUES.USERS) {
        searchUsers(value, 1);
        jump(1);
      }
      if (currentTab === TAB_VALUES.ADMINS) {
        searchAdmins(value, 1);
      }
    }, SEARCH_TIMER_INTERVAL);
    const mappedFilters = mapUserFilters({ searchFilter: value, page: 1 });
    fillSearchParams(setSearchParams, mappedFilters);
    setSearch(value);
    setCurrentPage(1);
  };

  useEffect(() => {
    jump(currentPage);
  }, [currentData]);

  const handleAdminAction = (id: string, setModal: (show: boolean) => void) => {
    setSelectedAdmin(admins.docs.find((admin: Admin) => admin.id === id));
    setModal(true);
  };

  const renderTable = (selectedTab: number) => {
    if (selectedTab === TAB_VALUES.USERS) {
      return (
        <ListCard<KGPortalUser>
          data={currentData.map((user) => ({ ...user, id: String(user.id) }))}
          withDetails
          detailsPath={PATHS.USERS}
          detailsSearchParams={moreDetailUrlSearchParam}
          loading={loading}
          error={errorMessage}
          title={title}
          structure={TABLE_STRUCTURES.USER}
          searchEntered={!!search}
        />
      );
    }
    if (selectedTab === TAB_VALUES.ADMINS) {
      return (
        <ListCard<Admin>
          data={admins.docs}
          withActions={{
            edit: (editId) => handleAdminAction(editId, setShowEditAdminModal),
          }}
          loading={loading}
          error={errorMessage}
          title={title}
          structure={TABLE_STRUCTURES.ADMIN}
          searchEntered={!!search}
        />
      );
    }
    return (
      <CardWrapper title={title}>
        <ErrorDisplay inline details={'Invalid tab value found'} />
      </CardWrapper>
    );
  };

  useEffect(() => {
    // Watch the current tab
    // if tab changes, clear everything
    if (searchParams.get(TAB_PROP) !== currentTab.toString()) {
      const tab = parseInt(searchParams.get(TAB_PROP) || '', 10);
      setCurrentTab(tab);
      setLoading(true);
      // Handle values on tab change
      if (tab === TAB_VALUES.USERS) {
        setTopHat({ activeTab: 'tab-users' });
        setTitle('Users');
        // Ensure data is fetched in case page number doesn't change
        getUsers(1);
      }
      if (tab === TAB_VALUES.ADMINS) {
        setTopHat({ activeTab: 'tab-admins' });
        setTitle('Admins');
        // Ensure data is fetched in case page number doesn't change
        getAdmins(1);
      }
      // Set defaults
      setCurrentPage(1);
      setSearch('');
      clearInputs('search');
      setSelectedOffice(undefined);
      setSelectedCompany(undefined);
      if (timer) {
        setLoading(false);
        clearTimeout(timer);
      }
      return;
    }
    // When URL changes, we should change filters
    if (searchParams.get(PAGE_PROP) !== currentTab.toString()) {
      setCurrentPage(parseInt(searchParams.get(PAGE_PROP) || '1', 10));
    }
    if (searchParams.get(USER_FILTER_PARAMS.OFFICE) !== selectedOffice?.toString()) {
      if (searchParams.get(USER_FILTER_PARAMS.OFFICE) === null) {
        setSelectedOffice(undefined);
      } else {
        setSelectedOffice(parseInt(searchParams.get(USER_FILTER_PARAMS.OFFICE)!));
      }
    }

    if (searchParams.get(USER_FILTER_PARAMS.COMPANY) !== selectedCompany?.toString()) {
      if (searchParams.get(USER_FILTER_PARAMS.COMPANY) === null) {
        setSelectedCompany(undefined);
      } else {
        setSelectedCompany(parseInt(searchParams.get(USER_FILTER_PARAMS.COMPANY)!));
      }
    }
  }, [searchParams]);

  useEffect(() => {
    // Handle page changes
    if (!loading) {
      setLoading(true);
      if (currentTab === TAB_VALUES.USERS) {
        if (search) {
          searchUsers(search);
        } else {
          getUsers();
        }
        jump(currentPage);
      }
      if (currentTab === TAB_VALUES.ADMINS) {
        if (search) {
          searchAdmins(search);
        } else {
          getAdmins();
        }
      }
    }

    const searchParams = mapUserFilters({});
    fillSearchParams(setSearchParams, searchParams);
  }, [currentPage, selectedOffice, selectedCompany]);

  const clickHandler = () => {
    navigate(`${PATHS.ADMIN_LOG}?${PAGE_PROP}=${FIRST_PAGE_NUMBER}`);
  };

  const getOffices = async () => {
    setLoading(true);
    try {
      const { data } = await client.query<GetOfficesQuery>({
        query: GetOfficesDocument,
      });

      if (data && data.officeListV2) {
        setOffices(data.officeListV2);
        setErrorMessage(undefined);
      }
    } catch (_error) {
      setErrorMessage('Error loading offices');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getOffices();
  }, []);

  const clearFilters = () => {
    setCurrentPage(1);
    jump(1);
    setSelectedOffice(undefined);
    setSelectedCompany(undefined);
  };

  return (
    <div className='grid'>
      <div className='col-12 col-md-3'>
        <SectionCard>
          <SearchBox
            id='search-user'
            placeholder='Search for...'
            disabled={false}
            showLabel={false}
            focusOnRender
            fullWidth
            value={search}
            onChange={handleSearch}
            onSubmit={(value) => handleSearch(value as string)}
            defaultValue={search}
          />
          <Button type='primary' className='button-with-icon' fullWidth onClick={clickHandler}>
            <i className='fa-solid fa-eye' /> Admin Log
          </Button>
          {currentTab === TAB_VALUES.USERS && (
            <>
              <div className='divider' />
              <div className={`${styles[`${block}__filters`]}`}>
                <h2 className={`${styles[`${block}__filters-title`]}`}>{'Filters'}</h2>
                {(selectedOffice || selectedCompany) && (
                  <button
                    type='button'
                    className={`text-with-icon ${styles[`${block}__filters-button`]}`}
                    onClick={() => clearFilters()}>
                    <i className='fa-solid fa-trash' />
                    {'Clear'}
                  </button>
                )}
              </div>
              <RadioList
                radioOptions={formatCompanyRadioOptions(COMPANIES)}
                title={'Company'}
                handleOnClick={(value) => {
                  setCurrentPage(1);
                  jump(1);
                  setSelectedCompany(value as number);
                }}
                radioName='Company'
                currentValue={selectedCompany}
              />
              <RadioList
                radioOptions={formatOfficeRadioOptions(offices)}
                title={'Office'}
                handleOnClick={(value) => {
                  setCurrentPage(1);
                  jump(1);
                  setSelectedOffice(value as number);
                }}
                radioName='Office'
                currentValue={selectedOffice}
              />
            </>
          )}
        </SectionCard>
      </div>
      <div className='col-12 col-md-9'>{renderTable(currentTab)}</div>
      <div className='col-12 content-middle content-right-md'>
        <Pagination
          count={currentTab === TAB_VALUES.USERS ? users.length : admins.totalDocs}
          perPage={PAGE_SIZE}
          currentPage={currentPage - 1}
          onChange={(value) => {
            setCurrentPage(value + 1);
            jump(value + 1);
          }}
        />
      </div>
      <EditAdminModal
        show={showEditAdminModal}
        admin={selectedAdmin}
        onClose={() => setShowEditAdminModal(false)}
        onSuccess={() => getAdmins(1)}
      />
    </div>
  );
};
export default Users;
